import React, { useState, useCallback, useEffect } from "react";
import { Form, Card, Row, Col, Button } from "react-bootstrap";
import { Formik } from "formik";
import {
  savePreferenceServer,
  loadPreferenceServer,
  getOveragePricing,
} from "src/docuclipper/api";
import { useDispatch } from "react-redux";
import { createAlert } from "src/redux/reducers/Alerts";

interface PricingInfo {
  pages: number;
  pricePerPage: number;
}

interface PagePurchaseProps {
  onPurchase?: (pages: number) => void;
}

export const AutomatedPagePurchases: React.FC<PagePurchaseProps> = ({
  onPurchase,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [pricing, setPricing] = useState<PricingInfo | null>(null);
  const [initialValues, setInitialValues] = useState({
    enabled: false,
  });
  const [isInitializing, setIsInitializing] = useState(true);
  const dispatch = useDispatch();

  useEffect(() => {
    const initialize = async () => {
      try {
        // Fetch pricing from backend
        const pricingResponse = await getOveragePricing();
        setPricing(pricingResponse);

        // Load automated purchase settings
        const response = await loadPreferenceServer({
          key: "overageConfig",
        });
        if (response && response.value) {
          const parsedValue = JSON.parse(response.value);
          setInitialValues({
            enabled: parsedValue.enabled || false,
          });
        }
      } catch (error) {
        console.error("Error initializing:", error);
        dispatch(
          createAlert({
            message: "Error loading configuration",
            timeout: 0,
            type: "error",
          })
        );
      } finally {
        setIsInitializing(false);
      }
    };

    initialize();
  }, [dispatch]);

  const handleAutomatedSubmit = useCallback(
    async (values, { setSubmitting }) => {
      setIsLoading(true);
      const { enabled } = values;
      try {
        await savePreferenceServer({
          key: "overageConfig",
          value: JSON.stringify({
            enabled,
          }),
        });
      } catch (error) {
        dispatch(
          createAlert({
            message: "Error saving configuration",
            timeout: 0,
            type: "error",
          })
        );
      } finally {
        setIsLoading(false);
        setSubmitting(false);
      }
    },
    [dispatch]
  );

  if (isInitializing) {
    return <div>Loading configuration...</div>;
  }

  return (
    <div>
      <div className="mb-4">
        {pricing && (
          <div className="mb-2">
            Auto-refill will purchase {pricing.pages.toLocaleString()} pages at
            ${pricing.pricePerPage.toFixed(2)} per page
            <div className="mt-1">
              <strong>
                Total: ${(pricing.pages * pricing.pricePerPage).toFixed(2)} USD
              </strong>
            </div>
          </div>
        )}
      </div>

      <Formik
        initialValues={initialValues}
        onSubmit={handleAutomatedSubmit}
        enableReinitialize
      >
        {({ values, handleChange, submitForm }) => (
          <Form>
            <Button
              variant={values.enabled ? "outline-success" : "outline-primary"}
              onClick={() => {
                handleChange({
                  target: { name: "enabled", value: !values.enabled },
                });
                submitForm();
              }}
              disabled={isLoading}
            >
              {values.enabled ? "Auto-Refill Enabled ✓" : "Enable Auto-Refill"}
            </Button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default AutomatedPagePurchases;
